import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { AppPicker } from "@/modules/shell/AppPicker";
import { listPayMethods, type PayMethod } from "@/shared/api/pay-methods";

type Props = {
  companySlug: string;
  value: string | null;
  onChange: (id: string) => void;
  disabled?: boolean;
  className?: string;
};

/** Active pay methods; searchable picker; selects company default on first load */
export function PayMethodSelect({
  companySlug,
  value,
  onChange,
  disabled,
  className,
}: Props) {
  const [methods, setMethods] = useState<PayMethod[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPayMethods(companySlug)
      .then((d) => {
        if (cancelled) return;
        setMethods(d.methods);
        if (!value) {
          const def =
            d.methods.find((m) => m.isDefault) ?? d.methods[0] ?? null;
          if (def) onChange(def.id);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed");
        }
      });
    return () => {
      cancelled = true;
    };
    // Intentionally once per company — avoid re-picking when parent value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug]);

  const options = useMemo(
    () =>
      methods.map((m) => ({
        value: m.id,
        label: m.name,
        description: [
          m.isDefault ? "Default" : null,
          m.worksOffline ? null : "Needs network",
          m.code,
        ]
          .filter(Boolean)
          .join(" · "),
        keywords: `${m.code} ${m.name}`,
      })),
    [methods],
  );

  if (error) {
    return <Alert variant="destructive" className="py-2 text-xs">{error}</Alert>;
  }

  return (
    <AppPicker
      className={className}
      title="Pay method"
      description="Search pay methods"
      placeholder={methods.length === 0 ? "Loading…" : "Choose pay method"}
      value={value}
      options={options}
      disabled={disabled || methods.length === 0}
      emptyLabel="No pay methods"
      onChange={(id) => {
        if (id) onChange(id);
      }}
    />
  );
}
